import React, { useEffect, useState } from 'react';
import { Animated, AppState, useWindowDimensions, View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import { AccountMenuItem } from '@/components/ui/therapist/CollapsibleSidebar/AccountMenuItem';
import { IconName } from '@/components/Icon';
import { IconName as NeolexonIconName } from '@/components/NeolexonIcon/Icons/types';
import { IS_WEB } from '@/utils/platform';
import { MenuItem } from '@/components/ui/therapist/CollapsibleSidebar/MenuItem';
import { SubMenu } from '@/components/ui/therapist/CollapsibleSidebar/SubMenu';
import { TherapistStackParamList } from '@/navigations/TherapistStack';
import { useSidebarAnimation } from '@/components/ui/therapist/CollapsibleSidebar/SidebarAnimationContext';
import { Links } from '@/config/links';
import { useStoreState } from '@/stores/store';
import constants from '@/config/constants';

import { styles } from './styles';
import colors from '@/config/colors';

export interface SubMenuItem {
  label: string;
  screen: keyof TherapistStackParamList | '';
  icon?: IconName;
  url?: string;
  filterForDemo?: boolean;
  screensActive?: string[];
}

interface SidebarMenuItem {
  icon: IconName | NeolexonIconName;
  label: string;
  screen?: keyof TherapistStackParamList | '';
  screensActive?: string[];
  subItems?: SubMenuItem[];
}

const menuItems: {
  icon: IconName | NeolexonIconName;
  label: string;
  newNavigation: boolean;
  screen?: keyof TherapistStackParamList | '';
  screensActive?: string[];
  url?: string;
}[] = [
  {
    icon: 'accountDetails',
    label: 'Meine Patient:innen',
    newNavigation: false,
    screen: 'Home',
    screensActive: [
      'Patient:innen',
      'Patienten-Profil',
      'Profil hinzufügen',
      'Profil bearbeiten',
    ],
  },
  {
    icon: 'bookShelf',
    label: 'Bibliothek',
    newNavigation: constants.appVersion === '1.10.29',
    screen: 'TemplateList',
    screensActive: ['Bibliothek'],
  },
];

export type SelectedNavItem = 'Hilfe' | 'Einstellungen' | '';

const baseSubItems: SubMenuItem[] = [
  { label: 'Über die App', screen: 'AboutNeolexon' },
  { label: 'Sperrbildschirm', screen: 'LockScreen', filterForDemo: true },
  { label: 'Meine Einwilligungen', screen: 'Consent', filterForDemo: true },
  {
    label: 'Datenschutzerklärung',
    screen: '',
    icon: 'upArrowBox',
    url: Links.datenschutz.href,
  },
  {
    label: 'Impressum',
    screen: '',
    icon: 'upArrowBox',
    url: Links.impressum.href,
  },
];

const subMenuItems: SidebarMenuItem[] = [
  {
    icon: 'helpCircle',
    label: 'Hilfe',
    screen: '',
    screensActive: ['Kontakt'],
    subItems: [
      {
        label: 'Bedienungsanleitung',
        screen: '',
        icon: 'upArrowBox',
        url: Links.anleitung.href,
      },
      {
        label: 'Schweigepflichtsentbindung',
        screen: '',
        icon: 'upArrowBox',
        url: Links.confidentiality.href,
      },
      { label: 'Kontakt', screen: 'Contact' },
    ],
  },
  {
    icon: 'lugnut',
    label: 'Einstellungen',
    screen: '',
    screensActive: ['Über die App', 'Sperrbildschirm', 'Meine Einwilligungen'],
    subItems: baseSubItems,
  },
];

export const CollapsibleSidebar: React.FC = () => {
  const { height } = useWindowDimensions();
  const [expandedSubMenu, setExpandedSubMenu] = useState<null | SubMenuItem[]>(
    null,
  );
  const [selectedNavItem, setSelectedNavItem] = useState<SelectedNavItem>('');
  const { sidebarWidthRef, sidebarWidthValue } = useSidebarAnimation();
  const { navigate } =
    useNavigation<StackNavigationProp<TherapistStackParamList>>();
  const { isTherapistDemo, isPatientDemo } = useStoreState((state) => state);

  const labelOpacity = sidebarWidthRef.interpolate({
    inputRange: [60, 120, 250],
    outputRange: [0, 0.2, 1],
    extrapolate: 'clamp',
  });

  const labelTranslateX = sidebarWidthRef.interpolate({
    inputRange: [60, 250],
    outputRange: [-10, 0],
    extrapolate: 'clamp',
  });

  const renderMenuItems = () => {
    return menuItems.map((item, idx) => {
      const { icon, label, newNavigation, screen, screensActive } = item;

      return (
        <MenuItem
          icon={icon}
          key={idx}
          label={label}
          labelOpacity={labelOpacity}
          labelTranslateX={labelTranslateX}
          newNavigation={newNavigation}
          onPress={() => screen && navigate(screen as any)}
          screensActive={screensActive}
        />
      );
    });
  };

  const subItemFilter = (item: SubMenuItem) => {
    const isDemo = isTherapistDemo || isPatientDemo;
    return !(isDemo && item.filterForDemo);
  };

  const renderSubMenuButtons = () => {
    return subMenuItems.map((item, idx) => {
      const { icon, label, screensActive } = item;
      return (
        <MenuItem
          icon={icon}
          key={idx}
          label={label}
          labelOpacity={labelOpacity}
          labelTranslateX={labelTranslateX}
          onPress={() => {
            if (item.subItems) {
              const subItems = item.subItems.filter(subItemFilter);
              setExpandedSubMenu(subItems);
              setSelectedNavItem(label as SelectedNavItem);
            } else if (item.screen) {
              navigate(item.screen as any);
            }
          }}
          outerStyle={{ paddingBottom: 10 }}
          screensActive={screensActive}
          selectedNavItem={selectedNavItem}
          showChevron
        />
      );
    });
  };

  useEffect(() => {
    const subscription = AppState.addEventListener('change', (nextAppState) => {
      if (nextAppState === 'background') {
        setExpandedSubMenu(null);
        setSelectedNavItem('');
      }
    });

    return () => subscription.remove();
  }, []);

  return (
    <>
      <Animated.View
        style={[
          styles.sidebar,
          { width: sidebarWidthValue, height: IS_WEB ? height - 88 : '100%' },
        ]}
      >
        <View style={{ paddingTop: 50 }}>{renderMenuItems()}</View>
        <View
          style={{
            marginTop: 'auto',
            borderBottomColor: colors.newUI.therapist.primaryBlue80,
            borderBottomWidth: 2,
          }}
        >
          {renderSubMenuButtons()}
        </View>
        <AccountMenuItem
          labelOpacity={labelOpacity}
          labelTranslateX={labelTranslateX}
        />
      </Animated.View>
      <SubMenu
        subMenu={expandedSubMenu}
        setExpandedSubMenu={() => {
          setExpandedSubMenu(null);
          setSelectedNavItem('');
        }}
      />
    </>
  );
};
